// src/features/manual/utils/labels.ts

import type { UserRole } from '@/types/models';
import type { ManualIndex, ManualLang, ManualScreenLink, ManualSection } from '../types';

/** The manual's parts, as the API orders them (roommatik.analytics `ManualParts.Ordered`). */
export const MANUAL_PARTS = [
  'getting-started',
  'reception',
  'management',
  'finance',
  'incidents',
  'systems',
  'administration',
  'reference',
] as const;

export const partTitleKey = (part: string): string =>
  (MANUAL_PARTS as readonly string[]).includes(part) ? `manual.parts.${part}` : 'manual.parts.other';

/** The entries listed under the open chapter in the index: its level-2 sections. */
export const railSections = (sections: ManualSection[]): ManualSection[] =>
  sections.filter((section) => section.level === 2 && Boolean(section.anchor));

/** The manual follows the interface language; anything other than English reads Spanish. */
export const manualLangFromLocale = (locale: string | undefined): ManualLang =>
  (locale ?? '').toLowerCase().startsWith('en') ? 'en' : 'es';

/**
 * Roommatik staff, by the effective role (an impersonated customer is not staff). The API decides
 * what is sent; this only decides whether the page draws staff boxes it received, as a second guard.
 */
export const isManualStaff = (role: UserRole | null): boolean => role === 'SuperAdmin' || role === 'Admin';

export const findScreenLink = (index: ManualIndex, screen: string): ManualScreenLink | null =>
  index.screens?.find((entry) => entry.screen === screen) ?? null;

/** The DOM id of a section heading, the target of rail links and «Ver en el manual». */
export const sectionDomId = (anchor: string): string => `manual-${anchor}`;

/** The route and router state that open a chapter, at a section when `anchor` is given (see ManualPage). */
export const manualSectionTarget = (
  chapterId: string,
  anchor: string | null
): { to: string; state: { anchor: string | null } } => ({
  to: `/manual/${encodeURIComponent(chapterId)}`,
  state: { anchor },
});
